import React from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowUp } from 'lucide-react';
import logo from '../assets/logo.JPG';
import AnimateOnScroll from './AnimateOnScroll';

const Footer = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Inventory', href: '#inventory' },
    { name: 'Contact', href: '#contact' },
  ];

  const serviceLinks = [
    'Luxury Car Sales', 'Warranty & Protection', 'Delivery & Pickup', '24/7 Support',
  ];

  const socials = [
    { icon: Facebook, label: 'Facebook' },
    { icon: Twitter, label: 'Twitter' },
    { icon: Instagram, label: 'Instagram' },
    { icon: Linkedin, label: 'LinkedIn' },
  ];

  return (
    <footer className="relative bg-gray-950 border-t border-white/5">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3DED97]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-14">
          <AnimateOnScroll animation="fade-up" className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src={logo} alt="SharpCars Logo" className="h-14 w-22 rounded-lg object-cover" />
              <span className="font-display text-2xl font-bold">
                Sharp<span className="gradient-text">Cars</span>
              </span>
            </div>
            <p className="text-gray-500 mb-8 leading-relaxed max-w-sm text-sm">
              We sell all types of luxury cars with the best quality guarantee. Experience
              automotive excellence with our premium collection and exceptional service.
            </p>
            <div className="flex gap-3">
              {socials.map(({ icon: Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  className="p-3 rounded-xl glass-card text-gray-500 hover:text-[#3DED97] hover:border-[#3DED97]/30 transition-all duration-300 hover:-translate-y-1"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-up" delay={100} className="lg:col-span-3 lg:col-start-7">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-gray-500 hover:text-[#3DED97] text-sm transition-colors duration-300 flex items-center gap-2 group">
                    <span className="w-0 group-hover:w-3 h-px bg-[#3DED97] transition-all duration-300" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-up" delay={200} className="lg:col-span-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-6">Our Services</h3>
            <ul className="space-y-3">
              {serviceLinks.map((service) => (
                <li key={service}>
                  <a href="#services" className="text-gray-500 hover:text-[#3DED97] text-sm transition-colors duration-300 flex items-center gap-2 group">
                    <span className="w-0 group-hover:w-3 h-px bg-[#3DED97] transition-all duration-300" />
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </AnimateOnScroll>
        </div>

        <AnimateOnScroll animation="fade-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-8 border-t border-white/5">
            {[
              { icon: Phone, label: 'Phone', value: '08066312634' },
              { icon: Mail, label: 'Email', value: 'info@sharpcars.ng' },
              { icon: MapPin, label: 'Address', value: 'Jesse Jackson Street, Abuja' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-4 p-4 rounded-xl glass-card">
                <div className="p-2.5 rounded-lg bg-[#3DED97]/10">
                  <Icon className="h-4 w-4 text-[#3DED97]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                  <p className="text-white font-medium text-sm">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimateOnScroll>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-xs text-center md:text-left">
            &copy; {currentYear} SharpCars. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-gray-600 hover:text-[#3DED97] text-xs transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-600 hover:text-[#3DED97] text-xs transition-colors">Terms of Service</a>
            <button
              onClick={scrollToTop}
              className="p-2.5 rounded-xl gradient-bg text-black hover:shadow-glow transition-all duration-300 hover:-translate-y-1"
              aria-label="Scroll to top"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
